import _ from 'lodash'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { batch, useDispatch } from 'react-redux'
import { Redirect, Route, BrowserRouter as Router, Switch } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

/** TRANSITION */
import 'i18n.js'

/** API */
import AboutUs from 'api/admin/clinic-manage/about.api'
import PersonnelApi from 'api/setting/personnel-managements/personnel.api'
import { menuFirsts, menuInside } from 'constants/menus'
import routes from 'routes'
import { routeName } from 'routes/routes-name'

/** COMPONENT */
import Header from 'component/Header/Header'
import Sidebar from 'component/SlideMenu/Sidebar'
import ConfirmAppointment from 'features/auth/confirm-appointment'
import Login from 'features/auth/login'
import ResetPassword from 'features/auth/reset-password'
import LineCallback from 'features/counter/register/line/line-callback'
import PageExpiredWebsite from 'features/page/expiredWebsite'
import PageNotFound from 'features/page/notFound'
import PermissionDenied from 'features/page/permissionDenied'
import Pick from 'features/pick/pick'


/** UTILS | CONTEXT  */
import { editBranchInfo, editMe } from 'app/slice/user.slice'
import { KEY_STORAGE, reloadClinicInfoAsMinute } from 'constants/common'
import { ThemeIcon } from 'constants/theme'
import { checkRememberLogin } from 'context/login-context'
import { checkPackage, getBranch, getClinicInfo, getPackageExpired, getPermissionMenus, getPermissionShortcuts, getPermissions, getSecureItem, getServicePoint, getToken, isTokenExpired, logout, setSecureItem, syncAddonSettingsFromApi } from 'utils/app.utils'

/** STYLE  */
import { styled } from '@mui/material/styles'
import { Config } from 'config'
import { scale } from 'constants/scale'
import ChangeCost from 'features/approvel-list/list/change-cost'

import OutstandingPayment from 'features/finance/payment/outstanding-payment'
import PaymentDetail from 'features/finance/payment/payment-detail'
import AnnouncementPopup from 'component/AnnouncementPopup/AnnouncementPopup'
import WacomConnectionToast from 'services/wacom/WacomConnectionToast'

const ContainerComponent = styled('div')(({ theme }) => ({
  '&.no-appBar': {
    top: '0 !important',
    height: '100vh !important'
  }
}))

const ContainerMenu = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '0',
  zIndex: 1200,
  [theme.breakpoints.up('lg')]: {
    width: '100%',
    maxWidth: `calc(${scale.sidebarSize})`
  },
  [theme.breakpoints.up('xl')]: {
    width: '100%',
    maxWidth: scale.sidebarSize
  }
}))

const ContainerContent = styled('div')(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('lg')]: {
    maxWidth: `calc(100% - ${scale.sidebarSize})`
  },
  '> div': {
    width: '100% !important',
    display: 'block !important',
    minHeight: 'unset !important',
    border: 'none !important',
    boxShadow: 'none important',
    marginTop: '0 !important'
  },
  '& .card-header': {
    width: '100% !important',
    height: 'auto !important'
  }
}))

// Persists the active route's permission to storage on mount/route change.
// Replaces a per-render setSecureItem that ran for every route on every App render.
function RoutePermissionEffect({ activeMenu }: { activeMenu: any }) {
  useEffect(() => {
    const permissions = { ...getPermissions(), activeMenu }
    setSecureItem(KEY_STORAGE.PERMISSION, JSON.stringify(permissions))
  }, [activeMenu])
  return null
}

const PICK_REDIRECT_PATHS = [
  'reset-password',
  'confirm-appointment',
  'approvel-list/change-cost/?',
  'approvel-list/cancel-reciept/?',
  'approvel-list/request-overdue/?'
]

function PickRoute({ path, currPath, token, branchId, servicePointId, permissionMenus, permissionShortcuts, clinicInfo }: any) {
  if (PICK_REDIRECT_PATHS.some((p) => currPath.includes(p))) {
    return <Redirect to={window.location.pathname} />
  }
  if (token && (!branchId || !servicePointId || !permissionMenus || !permissionShortcuts || !clinicInfo)) {
    return <Redirect to={path} />
  }
  logout()
  return <Redirect to={routeName.login} />
}

export default function App() {
  const dispatch = useDispatch()
  const [webExpired, setWebExpired] = useState(false)
  const [loadPermission, setLoadPermission] = useState(true)
  const [menuKeys, setMenuKeys] = useState<string[]>([menuInside.DOCTOR_EMR_HISTORY.key, menuFirsts.WELCOME.key])
  const [menuPermissions, setMenuPermissions] = useState<any[]>([])
  const [position, setPosition] = useState('')
  const [languageCurrent] = useState(getSecureItem(KEY_STORAGE.LANGUAGE) || 'TH')

  const currPath = window.location.href
  const token = getToken()
  const branchId = getBranch()
  const servicePointId = getServicePoint()
  const permissionMenus = getPermissionMenus()
  const permissionShortcuts = getPermissionShortcuts()
  const clinicInfo = getClinicInfo()

  const userIdFromParam = window.location?.search?.substring(1)
  const pathReportDf = window.location?.pathname === '/report/df-lab'
  const pathReportLabOperative = window.location?.pathname === '/report/df-lab-operative'

  // O(1) lookup, replaces 3 sequential _.find calls per route per render
  const menuPermissionMap = useMemo(() => {
    const map = new Map<string, any>()
    for (const m of menuPermissions) {
      const key = m.menuFirstKey || m.menuSecondKey || m.menuThirdKey
      if (key) map.set(key, m)
    }
    return map
  }, [menuPermissions])

  // O(1) membership check, replaces _.includes(menuKeys, key)
  const menuKeysSet = useMemo(() => new Set(menuKeys), [menuKeys])
  const checkPermission = useCallback((key: string) => menuKeysSet.has(key), [menuKeysSet])

  const autoLogout = useCallback(async () => {
    await logout()
    if (!currPath.includes('login')) window.location.href = routeName.login
    return <Redirect to={routeName.login} />
  }, [currPath])

  // Was set on every render
  useEffect(() => {
    document.title = `${Config.REACT_APP_APP_NAME}`
  }, [])

  // Was set on every render
  useEffect(() => {
    moment.locale(languageCurrent.toLowerCase())
  }, [languageCurrent])

  useEffect(() => {
    const favicon: any = document.querySelector('[rel=icon]')
    const apFavicon: any = document.querySelector('[rel=apple-touch-icon]')
    if (favicon) favicon.href = ThemeIcon.Favicon
    if (apFavicon) apFavicon.href = ThemeIcon.Favicon
  }, [])

  // Profile fetch — was triggered N times per App render from inside PrivateRoute
  useEffect(() => {
    if (!token) return
    PersonnelApi.findProfile()
      .then(({ data }) => {
        if (data) {
          setPosition(data.position.positionName)
          dispatch(editMe({ value: data, key: 'me' }))
        } else {
          autoLogout()
        }
      })
      .catch(() => autoLogout())
  }, [token])

  // Package expiration check
  useEffect(() => {
    const day = getPackageExpired()
    if (!day) return
    const diff = moment(day).add(1, 'days').diff(moment().subtract(1, 'days'), 'days')
    setWebExpired(diff <= 0)
  }, [])

  // One-shot bootstrap: remember-login, clinic info refresh, menu permissions, addons
  useEffect(() => {
    checkRememberLogin()

    // Refresh clinic info if stale
    const lastUpdateRaw = _.get(clinicInfo, 'lastUpdate')
    const lastUpdate = lastUpdateRaw ? moment(lastUpdateRaw, 'YYYY-MM-DD HH:mm') : null
    const duration = lastUpdate ? moment.duration(moment().diff(lastUpdate)).asMinutes() : 0
    if (duration >= reloadClinicInfoAsMinute && branchId) {
      AboutUs.detailClinic(Number(branchId))
        .then(({ data }) => {
          dispatch(editBranchInfo({ value: { data } }))
          setSecureItem(KEY_STORAGE.CLINIC_INFO, JSON.stringify({ ...data, lastUpdate: moment().format('YYYY-MM-DD HH:mm') }))
        })
        .catch(() => undefined)
    }

    // Flatten menu tree in a single pass — was 3-deep _.map with stale-closure setMenuKeys
    const allMenusKey: string[] = []
    const menuPermission: any[] = []
    if (permissionMenus) {
      for (const menu of permissionMenus) {
        allMenusKey.push(menu.menuFirstKey)
        menuPermission.push(_.omit(menu, 'menuSeconds'))
        for (const menu2 of menu.menuSeconds || []) {
          allMenusKey.push(menu2.menuSecondKey)
          menuPermission.push(_.omit(menu2, 'menuThirds'))
          for (const menu3 of menu2.menuThirds || []) {
            allMenusKey.push(menu3.menuThirdKey)
            menuPermission.push(menu3)
          }
        }
      }
    }
    batch(() => {
      setMenuPermissions(menuPermission)
      setMenuKeys((prev) => [...prev, ...allMenusKey])
      setLoadPermission(false)
    })

    if (token && branchId) syncAddonSettingsFromApi()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Token expiration polling
  useEffect(() => {
    if (!token) return
    const checkTokenExpiration = async () => {
      if (isTokenExpired()) await autoLogout()
    }
    checkTokenExpiration()
    const interval = setInterval(checkTokenExpiration, 60000)
    return () => clearInterval(interval)
  }, [token])

  // Memoized route tree — recomputes only when its real deps change
  const privateRoutes = useMemo(() => {
    if (webExpired) return [<Route key="PageExpiredWebsite" component={PageExpiredWebsite} />]

    return routes.map(({ name, path, component, appBar, textName, key, packageKey }, idx) => {
      const activeMenu = menuPermissionMap.get(key)
      const allowed = checkPermission(key) && checkPackage(packageKey)
      const reportBypass = userIdFromParam && (pathReportDf || pathReportLabOperative)
      return (
        <Route exact path={path} key={name || idx}>
          <RoutePermissionEffect activeMenu={activeMenu} />
          {appBar && <Header menuName={textName} position={position} />}
          <ContainerComponent className={`div-component d-flex ${appBar ? 'flex-row' : 'flex-column no-appBar'}`}>
            {appBar && <ContainerMenu className={'container-menu'}><Sidebar /></ContainerMenu>}
            <ContainerContent className={`container-content ${!appBar && 'mw-100'}`}>
              {!loadPermission &&
                ((allowed || reportBypass)
                  ? component && React.createElement(component)
                  : (key === 'BACKEND' && component && React.createElement(component)) || <PermissionDenied />)}
            </ContainerContent>
          </ContainerComponent>
        </Route>
      )
    })
  }, [webExpired, menuPermissionMap, checkPermission, position, loadPermission, userIdFromParam, pathReportDf, pathReportLabOperative])

  const isPrivateReady = !!(token && branchId && servicePointId && permissionMenus && permissionShortcuts && !_.isEmpty(clinicInfo))

  return (
    <div style={{ display: 'contents' }} className='private-route-container'>
      <Router>
        <Switch>
          {currPath.includes('line-connect/callback') && <Route key="lineCallback" path={`${routeName.register}/line-connect/callback`} component={LineCallback} />}
          {currPath.includes('reset-password') && <Route key="reset-password" path={routeName.resetPassword} component={ResetPassword} />}
          {currPath.includes('confirm-appointment') && <Route key="confirm-appointment" path={routeName.confirmAppointment} component={ConfirmAppointment} />}
          {currPath.includes('approvel-list/change-cost/?') && <Route key="change-cost" path={routeName.approvelListCost} component={ChangeCost} />}
          {currPath.includes('approvel-list/cancel-reciept/?') && <Route key="cancel-reciept" path={routeName.approvelListCancelReciept} component={PaymentDetail} />}
          {currPath.includes('approvel-list/request-overdue/?') && <Route key="request-overdue" path={routeName.approvelListOutstandingPayment} component={OutstandingPayment} />}
          {isPrivateReady ? privateRoutes : (
            <>
              <Route key="login" path={routeName.login} component={Login} />
              <Route exact path={routeName.pick} component={Pick} />
              <PickRoute
                key="pick"
                path={routeName.pick}
                currPath={currPath}
                token={token}
                branchId={branchId}
                servicePointId={servicePointId}
                permissionMenus={permissionMenus}
                permissionShortcuts={permissionShortcuts}
                clinicInfo={clinicInfo}
              />
            </>
          )}
          <Route key="PageNotFound" component={PageNotFound} />
        </Switch>
      </Router>
      <ToastContainer enableMultiContainer />
      <AnnouncementPopup />
      <WacomConnectionToast />
    </div>
  )
}
